import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { OwnershipFlags } from '@risksmart-app/trpc/src/routers/frontend/shared/ownership-filter-input';
import type { MyDueItemsResponse } from '@risksmart-app/trpc/src/types';
import {
  GetMyDueItemsDocument,
  type GetMyDueItemsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { getQueryVariables } from '@/utils/myItemsFilterConditions';

/**
 * Two classes of gap, both owned by COI-817 (a `packages/trpc` / `packages/drizzle`
 * change, which is why it is not COI-600). The ticket holds the analysis.
 */
type MyDueItemsSelectionGaps =
  // COI-817 — mis-typed only: the server returns these, but `MyDueItemsResponse`
  // aliases the wrong query-config family so they are unreachable from here.
  | 'assessment.TargetCompletionDate'
  | 'control.scheduleState'
  | 'document.scheduleState'
  | 'issue.assessment'
  | 'obligation.scheduleState'
  | 'risk.scheduleState'
  // COI-817 — genuinely absent: the due-items query configs don't select them,
  // so a consumer correctly sees `undefined`.
  | 'attestation_record.attestationRecordStatus'
  | 'change_request.RequestedChanges'
  | 'change_request.Workflow';

export const mapTrpcMyDueItemsToGraphQL = (
  trpcData: MyDueItemsResponse
): StrictMappedGraphQLSelection<
  GetMyDueItemsQuery,
  MyDueItemsSelectionGaps
> => {
  return {
    action: trpcData.action,
    assessment: trpcData.assessment,
    control: trpcData.control,
    document: trpcData.document,
    indicator: trpcData.indicator,
    issue: trpcData.issue,
    obligation: trpcData.obligation,
    risk: trpcData.risk,
    assessment_activity: trpcData.assessmentActivity,
    attestation_record: trpcData.attestationRecord,
    change_request: trpcData.changeRequest.map((cr) => ({
      ...cr,
      responses: cr.responses.map((r) => ({
        ...r,
        approver: r.approver,
      })),
    })),
  };
};

export type UseGetMyDueItemsArgs = {
  /** Pre-resolved UTC ISO instant — the "due by" horizon. */
  date: string;
  /**
   * The My Items 8-flag ownership model. Keeps the procedure's `ownershipFilter`
   * key rather than the shared `ownership` fragment name — renaming the wire is
   * a server change (COI-691 §3).
   */
  ownershipFilter: OwnershipFlags;
};

/**
 * All ten `*FilterConditions` variables are non-null in `getMyDueItems`, so each
 * is supplied on every call.
 *
 * There is no unfiltered call: `ownershipFilter` is required here and on the
 * procedure, and an all-false set yields `{ _or: [] }` — FALSE to Hasura, so an
 * empty feed. Deliberate parity with the tRPC leg, where "not supplied" and
 * "all eight false" are different values (`ownership-filter-input.ts`).
 * Expressing "every due item regardless of ownership" needs the procedure input
 * widened.
 */
const useMyDueItemsGraphqlVariables = ({
  date,
  ownershipFilter,
}: UseGetMyDueItemsArgs) => {
  const { user } = useRisksmartUser();
  const conditions = getQueryVariables(ownershipFilter, user?.userId);

  return {
    userId: user?.userId ?? '',
    date,
    riskFilterConditions: conditions.riskFilterConditions,
    actionFilterConditions: conditions.actionFilterConditions,
    assessmentFilterConditions: conditions.assessmentFilterConditions,
    controlFilterConditions: conditions.controlFilterConditions,
    issueFilterConditions: conditions.issueFilterConditions,
    assessmentActivityFilterConditions:
      conditions.assessmentActivityFilterConditions,
    documentFilterConditions: conditions.documentFilterConditions,
    indicatorFilterConditions: conditions.indicatorFilterConditions,
    obligationFilterConditions: conditions.obligationFilterConditions,
  };
};

const useGetMyDueItemsBase = createQueryHook<
  UseGetMyDueItemsArgs,
  MyDueItemsResponse,
  GetMyDueItemsQuery,
  MyDueItemsSelectionGaps
>({
  trpcQueryOptions: (trpc, { date, ownershipFilter }) =>
    trpc.frontend.myItems.dueItems.queryOptions({
      date,
      ownershipFilter,
    }),
  mapTrpcDataToGraphQL: mapTrpcMyDueItemsToGraphQL,
  graphqlDocument: GetMyDueItemsDocument,
  graphqlVariables: useMyDueItemsGraphqlVariables,
});

/**
 * Fetches the My Items "due by <date>" feed across all ten entity types.
 * The current user is resolved internally — callers pass the horizon and the
 * ownership flags only.
 */
export const useGetMyDueItems = (params: {
  queryArgs: UseGetMyDueItemsArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => useGetMyDueItemsBase(params);
